import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Plus, Trash2, ArrowDown, ArrowUp, Save, Power, GitBranch, Clock, ChevronDown,
} from "lucide-react";

const ENTITIES = ["scheme", "settlement", "exception"];

export default function WorkflowBuilder() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["Super Administrator", "Corporate Scheme Admin", "OpCo Scheme Manager"].includes(user?.role);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["workflows"],
    queryFn: async () => (await api.get("/workflows")).data,
  });
  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await api.get("/auth/roles")).data,
  });
  const roles = rolesData?.roles || [];

  useEffect(() => {
    if (!data) return;
    if (selId === "new") return;
    if (!selId && data.workflows.length) setSelId(data.workflows[0].id);
  }, [data, selId]);

  useEffect(() => {
    if (selId === "new") {
      setDraft({ name: "New Approval Workflow", entity: "scheme", stages: [], status: "draft" });
    } else if (selId && data) {
      const wf = data.workflows.find((w) => w.id === selId);
      if (wf) setDraft(JSON.parse(JSON.stringify(wf)));
    }
  }, [selId, data]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name: draft.name, entity: draft.entity,
        stages: draft.stages.map((s) => ({ id: s.id, name: s.name, role: s.role, sla_hours: Number(s.sla_hours) || 24 })) };
      if (selId === "new") return (await api.post("/workflows", payload)).data;
      return (await api.put(`/workflows/${selId}`, payload)).data;
    },
    onSuccess: (res) => {
      toast.success("Workflow saved");
      qc.invalidateQueries({ queryKey: ["workflows"] });
      if (selId === "new" && res?.id) setSelId(res.id);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const setStatus = useMutation({
    mutationFn: async (status) => (await api.patch(`/workflows/${selId}/status`, { status })).data,
    onSuccess: (r) => { toast.success(`Workflow ${r.status}`); qc.invalidateQueries({ queryKey: ["workflows"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/workflows/${selId}`)).data,
    onSuccess: () => { toast.success("Workflow deleted"); setSelId(null); qc.invalidateQueries({ queryKey: ["workflows"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const addStage = () => setDraft((d) => ({ ...d, stages: [...d.stages, { name: "New Stage", role: roles[0] || "Sales Reviewer", sla_hours: 24 }] }));
  const updateStage = (i, key, val) => setDraft((d) => { const st = [...d.stages]; st[i] = { ...st[i], [key]: val }; return { ...d, stages: st }; });
  const removeStage = (i) => setDraft((d) => ({ ...d, stages: d.stages.filter((_, x) => x !== i) }));
  const move = (i, dir) => setDraft((d) => {
    const st = [...d.stages]; const j = i + dir;
    if (j < 0 || j >= st.length) return d;
    [st[i], st[j]] = [st[j], st[i]]; return { ...d, stages: st };
  });

  if (isLoading || !data) return <Loading label="Loading workflow builder…" />;

  return (
    <div>
      <PageHeader title="Approval Workflow Builder" subtitle="Design multi-stage, no-code approval processes"
        actions={
          canEdit && (
            <Button data-testid="new-workflow-btn" onClick={() => setSelId("new")} className="gap-2 bg-[#0F2C59] hover:bg-[#00529B]">
              <Plus size={16} /> New Workflow
            </Button>
          )
        } />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Workflow list */}
        <div className="space-y-2">
          {data.workflows.map((w) => (
            <button key={w.id} data-testid={`workflow-item-${w.id}`} onClick={() => setSelId(w.id)}
              className={`w-full text-left p-3 rounded-xl border transition-colors ${selId === w.id ? "border-[#00529B] bg-blue-50/60 ring-1 ring-[#00529B]/20" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800 text-sm truncate">{w.name}</span>
                <Pill tone={w.status}>{w.status}</Pill>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400">
                <span className="capitalize px-1.5 py-0.5 rounded bg-slate-100">{w.entity}</span>
                <span>{w.stages?.length || 0} stages · v{w.version}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        {draft ? (
          <Panel testid="workflow-editor" title={selId === "new" ? "New Workflow" : "Edit Workflow"}
            actions={
              canEdit && (
              <div className="flex items-center gap-2">
                {selId !== "new" && draft.status !== "active" && (
                  <Button size="sm" variant="outline" className="gap-1.5 text-emerald-700 border-emerald-200" data-testid="activate-workflow-btn"
                    onClick={() => setStatus.mutate("active")}><Power size={14} /> Activate</Button>
                )}
                {selId !== "new" && draft.status === "active" && (
                  <Button size="sm" variant="outline" className="gap-1.5" data-testid="archive-workflow-btn"
                    onClick={() => setStatus.mutate("draft")}><Power size={14} /> Set Draft</Button>
                )}
                {selId !== "new" && (
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="delete-workflow-btn"
                    onClick={() => del.mutate()}><Trash2 size={14} /></Button>
                )}
                <Button size="sm" className="gap-1.5 bg-[#D97706] hover:bg-[#F59E0B]" data-testid="save-workflow-btn"
                  disabled={save.isPending} onClick={() => save.mutate()}><Save size={14} /> Save</Button>
              </div>
              )
            }>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className="text-slate-700 text-sm">Workflow Name</Label>
                <Input data-testid="workflow-name" className="mt-1.5" value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Applies To</Label>
                <select data-testid="workflow-entity" className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  value={draft.entity} onChange={(e) => setDraft({ ...draft, entity: e.target.value })}>
                  {ENTITIES.map((x) => <option key={x} value={x} className="capitalize">{x}</option>)}
                </select>
              </div>
            </div>

            {/* Visual stage flow */}
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Approval Stages</div>
            <div className="space-y-0" data-testid="workflow-stages">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs shrink-0">S</span>
                <span className="text-sm text-slate-500">Submitted</span>
              </div>
              {draft.stages.map((s, i) => (
                <div key={i} data-testid={`stage-${i}`}>
                  <div className="flex justify-center"><ChevronDown size={16} className="text-slate-300" /></div>
                  <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#00529B] transition-colors">
                    <span className="w-8 h-8 rounded-full adani-navy-gradient text-white flex items-center justify-center text-xs shrink-0 mt-1">{i + 1}</span>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input data-testid={`stage-name-${i}`} value={s.name} placeholder="Stage name"
                        onChange={(e) => updateStage(i, "name", e.target.value)} />
                      <select data-testid={`stage-role-${i}`} className="h-10 rounded-md border border-slate-200 px-2 bg-white text-sm"
                        value={s.role} onChange={(e) => updateStage(i, "role", e.target.value)}>
                        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <div className="relative">
                        <Clock size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input data-testid={`stage-sla-${i}`} type="number" className="pl-8" value={s.sla_hours}
                          onChange={(e) => updateStage(i, "sla_hours", e.target.value)} placeholder="SLA hrs" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp size={15} /></button>
                      <button onClick={() => move(i, 1)} disabled={i === draft.stages.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown size={15} /></button>
                      <button data-testid={`remove-stage-${i}`} onClick={() => removeStage(i)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex justify-center"><ChevronDown size={16} className="text-slate-300" /></div>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs shrink-0"><GitBranch size={14} /></span>
                <span className="text-sm text-slate-500">Approved / Completed</span>
              </div>
            </div>

            {canEdit && (
              <Button variant="outline" size="sm" className="gap-1.5 mt-4" data-testid="add-stage-btn" onClick={addStage}>
                <Plus size={14} /> Add Stage
              </Button>
            )}
          </Panel>
        ) : (
          <div className="flex items-center justify-center py-24 text-slate-400">Select or create a workflow to edit.</div>
        )}
      </div>
    </div>
  );
}
